import { fromJS, List, toJS } from 'immutable';
import {
  PRODUCT_TYPES,
  SECTION_TYPES,
  SPECIAL_OPS,
} from '../common/PicabooConstants';
import getPageNumber from '../helpers/PageNumber';
import {
  getProductText,
  getProjectAuthor,
  getProjectTitle,
} from '../utils/projectUtils';

export const currentProject = (state) => state.project.present;
export const userSelector = (state) => state.user;
export const tokenSelector = (state) => state.user.token;
export const userPrefSelector = (state) => state.user.get('preferences');
export const userPrefIgnoredWords = (state) =>
  state.user.get('preferences')?.get('ignoredWords') || [];
export const loginError = (state) => state.user.get('loginError');
export const isGuestUser = (state) => state.user && state.user.get('isGuest');
export const isGuestUserSelector = isGuestUser;
export const layoutSelector = (state) => state.layouts;
export const marketingBannerSelector = (state) => state.marketingBanner;
export const marketingBanner = (state) => state.marketingBanner;
export const isDesigner = (state) => {
  return (
    (state.user.get('specialOps') &
      (SPECIAL_OPS.DESIGNER_ARTWORK |
        SPECIAL_OPS.DESIGNER_ADMIN |
        SPECIAL_OPS.ADMIN)) !==
    0
  );
};
export const isDesignerSelector = isDesigner;
export const isSuperEditor = (state) => {
  return (
    (state.user.get('specialOps') &
      (SPECIAL_OPS.CS_EDIT | SPECIAL_OPS.CS_ADMIN)) !==
    0
  );
};
export const isSuperEditorSelector = isSuperEditor;
export const userFeatureOne = (state) => {
  return (state.user.get('specialOps') & SPECIAL_OPS.FEATURE_ONE) !== 0;
};
export const userFeatureOneSelector = userFeatureOne;
export const userFeatureTwo = (state) => {
  return (state.user.get('specialOps') & SPECIAL_OPS.FEATURE_TWO) !== 0;
};
export const userFeatureTwoSelector = userFeatureTwo;

export const preloadImagesSelector = (state) => state.preloader;
export const projectImagesSelector = (state) => state.images;
export const numberOfProjectImagesSelector = (state) =>
  Object.values(state.images.toJS()).filter((image) => image.serverImage === 0)
    .length;
export const projectPKSelector = (state) =>
  state.project.present.metadata.get('projectPK');
export const coverTypesSelector = (state) =>
  state.project.present.metadata.getIn(['preferences', 'coverType']);
export const coverSelector = (state) =>
  state.project.present.metadata.get('cover');
export const projectMetadataSelector = (state) =>
  state.project && state.project.present && state.project.present.metadata;
export const projectStyleSelector = (state) =>
  state.project.present.metadata.get('style');
export const projectLoading = (state) =>
  state &&
  state.project &&
  state.project.present &&
  state.project.present.metadata &&
  state.project.present.metadata.get('loadingProject');
export const projectSectionsSelector = (state) =>
  state.project.present.sections;
export const projectTitleSelector = (state) => {
  const project = projectMetadataSelector(state);
  return getProjectTitle(project);
};
export const projectAuthorSelector = (state) => {
  const project = projectMetadataSelector(state);
  return getProjectAuthor(project);
};
export const projectHasWarningsSelector = (state) => {
  const project = projectMetadataSelector(state);

  let ww = { spelling: null, lowResolution: null, emptyWell: null };
  const warningMap = project.get('warnings');
  if (warningMap && typeof warningMap !== 'undefined') {
    warningMap.forEach((value, key) => {
      ww[key] =
        value.size > 0
          ? value.toArray().map((cb) => {
              return cb;
            })
          : null;
    });
  }

  const hasWarnings =
    ww?.spelling?.length > 0 ||
    ww?.lowResolution?.length > 0 ||
    ww?.emptyWell?.length > 0;

  return hasWarnings;
};

export const projectNodesSelector = (state) => {
  return projectMetadataSelector(state).getIn(['imageMeta', 'nodeNames']);
};
export const projectProductInfoSelector = (state) =>
  state.project.present.productInfo;

const getPrice = (state, priceType) => {
  let pageCount = projectMetadataSelector(state).get('numPages');
  let paperType = projectMetadataSelector(state).get('paperTypeID');
  let subType = projectMetadataSelector(state).get('subtype');

  let pricing = state.project.present.productInfo.get('pricing');
  let price =
    pricing && pricing.getIn([pageCount, priceType, subType, paperType]);

  return price;
};

export const projectProductIDSelector = (state) => {
  return projectMetadataSelector(state).getIn(['product', 'id']);
};

export const perPageCostSelector = (state) => {
  return getPrice(state, 'additional_pages');
};

export const projectCostSelector = (state) => {
  return getPrice(state, 'books');
};

export const projectCostLogoUpgradeSelector = (state) => {
  return getPrice(state, 'books_with_logo_upgrade');
};

export const projectShareIdSelector = (state) => {
  return projectMetadataSelector(state).get('shareID');
};

export const pagesSelector = (state) => state.project.present.pages;
export const generatingThumbsSelector = (state) =>
  projectMetadataSelector(state).get('generatingThumbCount');
export const reviewPagesSelector = (state) =>
  state.project.present.reviewProject.get('pages');
export const reviewProjectSelector = (state) =>
  state.project.present.reviewProject;
export const pageItemsSelector = (state) => state.project.present.pageItems;
export const undoHistorySelector = (state) => state.project.past;
export const redoHistorySelector = (state) => state.project.future;
export const selectionSelector = (state) => state.selection;
export const selectedStylesSelector = (state) => state.selection.selectedStyles;
export const applyLayoutCallBack = (state) =>
  state.selection.applyLayoutCallback;
export const selectedPageItems = (state) =>
  new List(
    state.selection.pageItems
      .map((id) => state.project.present.pageItems.get(id))
      .filter((i) => i),
  );

export const selectedPageItemSelector = (state) =>
  selectedPageItems(state).first();

export const selectedPages = (state) =>
  new List(
    state.selection.pages
      .map(
        (id) =>
          state.project.present.pages.get(id) &&
          state.project.present.pages.get(id).filter((a) => !!a),
      )
      .filter((page) => {
        return !!page;
      }),
  );
export const selectedPhotoSelector = (state) => {
  const selectedItems = selectedPageItems(state);
  if (
    selectedItems.size == 1 &&
    selectedItems.get(0) &&
    selectedItems.get(0).get('imageID') != ''
  ) {
    let imageID = selectedItems.get(0).get('imageID');
    return state.images.get(imageID);
  } else {
    return undefined;
  }
};
export const productInfo = (state) =>
  state.project && state.project.present && state.project.present.productInfo;
export const imageUsage = (state) => state.project.present.imageUsage;
export const settings = (state) => state.settings;
export const sectionIdSelector = (state) => state.selection.section;
export const sectionSelector = (state) =>
  state.project.present.sections.get(state.selection.section);
export const sectionBodySelector = (state) =>
  state.project.present.sections.filter((section) => {
    return section.get('type') === SECTION_TYPES.TYPE_THEMED;
  });
export const reviewSectionSelector = (state) =>
  state.project.present.reviewProject
    ? state.project.present.sections.get(
        state.project.present.reviewProject.get('section'),
      )
    : null;
export const home = (state) => state.home;
export const navigationSelector = (state) => state.navigation;
export const editorDimSelector = (state) => state.navigation.editorDim;
export const wrapSelector = (state) => state.wrap;
export const selectedSize = (state) =>
  state.project.present.productInfo.get('availableSizes') &&
  state.project.present.productInfo
    .get('availableSizes')
    .find((o) => o.get('sku') === state.selection.selectedSKU);
export const designsSelector = (state) => state.designs;
export const imagesToUploadCount = (state) =>
  state.addPhotos.imagesToUpload.length;
export const imagesFailedPreResize = (state) => state.addPhotos.failedImages;
export const imagesFailedUpload = (state) => state.addPhotos.failedImageUploads;
export const pageNumbersSettings = (state) =>
  state.project.present.metadata.getIn(['preferences', 'pageNumberStyle']);
export const shareViewSelector = (state) => state.navigation.shareView;
export const gridViewSelector = (state) => state.navigation.gridView;
export const previewModeSelector = (state) => {
  return state.navigation.previewMode;
};
export const loadedImagesSections = (state) => state.loadedImages.sections;

export const permissionsSelector = (state) =>
  projectMetadataSelector(state).get('permissions');

export const leftReviewPageNumber = (state) => {
  const leftPage = state.project.present.reviewProject.get('pages')?.first();
  const sec = reviewSectionSelector(state);
  const sections = state.project.present.sections;
  const paperTypeID = state.project.present.metadata.get('paperTypeID');

  if (sec && leftPage) {
    return getPageNumber(leftPage.get('id'), sections, paperTypeID);
  } else {
    return 0;
  }
};

export const leftPageNumber = (state) => {
  const leftPage = pagesSelector(state).first();
  const sec = sectionSelector(state);
  const sections = state.project.present.sections;
  const paperTypeID = state.project.present.metadata.get('paperTypeID');

  if (sec && leftPage) {
    return getPageNumber(leftPage.get('id'), sections, paperTypeID);
  } else {
    return 0;
  }
};

export const projectPagesSelector = (state) => {
  const sections = projectSectionsSelector(state);
  let allPages = [];
  sections?.forEach((section) => {
    let spreads = section.get('spreads');
    if (spreads) {
      spreads.forEach((spread, index) => {
        spread.get('pages').forEach((p, spreadIndex) => {
          allPages.push({
            pageID: p.get('pageID'),
            ratioBase: p.get('ratioBase'),
            spreadID: spread.get('id'),
            sectionID: section.get('sectionID'),
          });
        });
      });
    }
  });
  return allPages;
};

export const projectSpreadsSelector = (state) => {
  const sections = projectSectionsSelector(state);
  let allSpreads = [];
  sections.forEach((section) => {
    if (section.has('spreads')) {
      let spreads = section.get('spreads');
      spreads.forEach((spread, index) => {
        spread = spread.set('sectionType', section.get('type'));
        allSpreads.push(spread);
      });
    }
  });
  return allSpreads;
};

export const activeSpreadSelector = (state) => {
  return state.project.present.sections.reduce((spreads, section) => {
    if (!section.has('spreads')) {
      return spreads;
    }
    return spreads.concat(
      section.get('spreads').map((spread) => {
        return spread.get('id');
      }),
    );
  }, List());
};

export const isBook = (state) =>
  [
    PRODUCT_TYPES.BOOK,
    PRODUCT_TYPES.YEARBOOK,
    PRODUCT_TYPES.PERSONALIZED_YEARBOOK,
  ].indexOf(projectMetadataSelector(state).get('type')) !== -1;
export const isSinglePageProduct = (state) =>
  [
    PRODUCT_TYPES.CRYSTAL,
    PRODUCT_TYPES.COLLAGE,
    PRODUCT_TYPES.PHOTO_PANEL,
    PRODUCT_TYPES.METAL_PRINT,
    PRODUCT_TYPES.MOUSE_PAD,
    PRODUCT_TYPES.BLANKET,
    PRODUCT_TYPES.JOURNAL,
    PRODUCT_TYPES.NOTEBOOK,
    PRODUCT_TYPES.COASTER,
    PRODUCT_TYPES.MUG,
    PRODUCT_TYPES.PUZZLE,
    PRODUCT_TYPES.FRAME,
    PRODUCT_TYPES.CANVAS,
    PRODUCT_TYPES.PHOTO_POSTER,
    PRODUCT_TYPES.YEARBOOK_AD,
  ].indexOf(projectMetadataSelector(state).get('type')) !== -1;
export const canChangeOrientation = (state) =>
  [
    PRODUCT_TYPES.CRYSTAL,
    PRODUCT_TYPES.COLLAGE,
    PRODUCT_TYPES.PHOTO_PANEL,
    PRODUCT_TYPES.METAL_PRINT,
    PRODUCT_TYPES.MOUSE_PAD,
    PRODUCT_TYPES.PUZZLE,
  ].indexOf(projectMetadataSelector(state).get('type')) !== -1;
export const isPrints = (state) =>
  projectMetadataSelector(state).get('type') === PRODUCT_TYPES.PRINTS;

export const currentSpreadIDSelector = (state) => {
  let sections = projectSectionsSelector(state);
  let currentSection = sectionSelector(state);
  let currentPages = pagesSelector(state);
  let leftPage = currentPages.first();
  let rightPage = currentPages.last();

  // sanity check here.. when we are changing cover types the new section id may not be loaded yet.. so don't puke!
  if (!currentSection) return null;

  let leftPageID = leftPage ? leftPage.get('id') : '';
  let rightPageID = rightPage ? rightPage.get('id') : '';
  let currentSpreadID = '';

  sections.map((section) => {
    if (currentSection.get('sectionID') === section.get('sectionID')) {
      section.get('spreads').map((spread, spreadIndex) => {
        const isCurrent = spread
          .get('pages')
          .toJS()
          .some((page) => {
            return leftPageID === page.pageID || rightPageID === page.pageID;
          });

        if (isCurrent) {
          currentSpreadID = spread.get('id');
        }
      });
    }
  });

  return currentSpreadID;
};

export const spreadIndexSelector = (state) => {
  let currentSpreadID = currentSpreadIDSelector(state);
  let spreads = activeSpreadSelector(state);
  let index = spreads.indexOf(currentSpreadID);

  return index;
};

export const validFontSelector = (state) => {
  let fonts = state.fonts;
  let validFontNames = [];
  if (fonts.fonts) {
    fonts.fonts.forEach((font) => {
      validFontNames.push(font.name);
      validFontNames.push(font.fontName);
    });
  }
  return validFontNames;
};

export const projectTypeSelector = (state) =>
  projectMetadataSelector(state).get('type');

export const overLayEnabledSelector = (state) =>
  state.selection.swapPhotoEnabled ||
  state.selection.shapeSpotterEnabled ||
  state.selection.backgroundPlacerEnabled ||
  state.selection.layoutPlacerEnabled ||
  state.selection.designPlacerEnabled;

export const projectProductSelector = (state) =>
  projectMetadataSelector(state).get('product');
export const projectNameSelector = (state) =>
  projectMetadataSelector(state).get('name');
export const isCoverSelector = (state) =>
  pagesSelector(state).every((page) => {
    return page.get('pageType') == 'C';
  });
export const productTextSelector = (state) => {
  const projectProduct = projectProductSelector(state);
  return getProductText(projectProduct) || null;
};

export const galleriesSelector = (state) => state.addPhotos.galleries.galleries;
export const galleryImagesSelector = (state) =>
  state.addPhotos.galleries.galleryImages;
export const selectedGallerySelector = (state) =>
  state.addPhotos.galleries.selectedGallery;
export const selectedGalleryPK = (state) => {
  const selectedGallery = galleriesSelector(state).find(
    (x) => x.name === selectedGallerySelector(state),
  );
  if (selectedGallery) {
    return selectedGallery.pk;
  }
  return null;
};

export const projectWarningsSelector = (state) => {
  return projectMetadataSelector(state)?.get('warnings');
};

export const pricingSelector = (state) => state.pricing;
export const errorSelector = (state) => state.error;

export const projectsSelector = (state) => state.addPhotos.projects;
